import React, { useRef, useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { COLORS } from '../../styles/tokens';
import { LIBRARIES, API_KEY, KEY_MISSING } from '../../utils/googleMapsKey';
import MapPinPicker from './MapPinPicker';
import SupplierLogo from './SupplierLogo';

// Returnerer { street, city, lat, lng }, og pinned: true når stedet er satt med pin på kartet.
// variant='field': designets 52 px-felt med ikon foran (icon = React-element). Standard er uendret.
// allowMyLocation: viser «Min posisjon»-knapp som fyller adressen fra enhetens GPS.
// allowPin: viser «Sett pin på kart» (for steder uten nøyaktig adresse). Skjules uten Google Maps-nøkkel.
// suggestions: [{ title, chips }] viser forslag i et panel under feltet når det får fokus, med «Min posisjon» først
// (allowMyLocation). Uten suggestions ligger «Min posisjon» som knapp under feltet, som før.
export default function AddressInput({ label, value, onChange, required, variant, icon, allowMyLocation, allowPin, suggestions }) {
  const field = variant === 'field';
  const [autocomplete, setAutocomplete] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');
  const [pinOpen, setPinOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const panelMode = Array.isArray(suggestions);
  const groups = panelMode ? suggestions.filter(g => g.chips?.length) : [];
  const showPanel = panelMode && panelOpen && (allowMyLocation || groups.length > 0);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: KEY_MISSING ? '' : API_KEY,
    libraries: LIBRARIES,
  });

  const pinAvailable = allowPin && isLoaded && !KEY_MISSING;
  // Skriving i feltet endrer adressen, så den er ikke lenger satt med pin
  const { pinned: _pinned, ...valueWithoutPin } = value;

  function fillFromMyLocation() {
    if (!navigator.geolocation) { setLocError('Posisjon støttes ikke på denne enheten'); setPanelOpen(false); return; }
    setLocating(true);
    setLocError('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const fallback = { street: 'Min posisjon', city: '', lat, lng };
        const geocoder = isLoaded && !KEY_MISSING && window.google?.maps?.Geocoder
          ? new window.google.maps.Geocoder()
          : null;
        if (!geocoder) { onChange(fallback); setLocating(false); setPanelOpen(false); return; }
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          const best = status === 'OK' ? results?.[0] : null;
          const city = best?.address_components
            ?.find(c => c.types.includes('postal_town') || c.types.includes('locality'))
            ?.long_name ?? '';
          onChange(best ? { street: best.formatted_address, city, lat, lng } : fallback);
          setLocating(false);
          setPanelOpen(false);
        });
      },
      () => {
        setLocError('Fikk ikke tilgang til posisjonen din. Sjekk tillatelser i nettleseren.');
        setLocating(false);
        setPanelOpen(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function onPlaceChanged() {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    const lat = place.geometry?.location?.lat() ?? null;
    const lng = place.geometry?.location?.lng() ?? null;
    const city = place.address_components
      ?.find(c => c.types.includes('postal_town') || c.types.includes('locality'))
      ?.long_name ?? '';

    onChange({ street: place.formatted_address ?? value.street, city, lat, lng });
  }

  function pickSuggestion(chip) {
    onChange({
      street: chip.street, city: chip.city, lat: chip.lat, lng: chip.lng,
      ...(chip.pinned ? { pinned: true } : {}),
    });
    setPanelOpen(false);
  }

  // Panelet lever så lenge fokus er i feltet eller inne i panelet, så det kan brukes med tastatur (Tab)
  function closePanelOnBlur(e) {
    const next = e.relatedTarget;
    if (next && (panelRef.current?.contains(next) || next === inputRef.current)) return;
    setPanelOpen(false);
  }

  function closePanelOnEscape(e) {
    if (e.key === 'Escape') setPanelOpen(false);
  }

  const inputStyle = field ? {
    padding: icon ? '0 14px 0 44px' : '0 14px', height: 52, borderRadius: 14, border: `1px solid ${COLORS.border}`,
    fontSize: 15.5, fontWeight: 600, width: '100%', boxSizing: 'border-box', background: COLORS.white, color: COLORS.textDark,
  } : {
    padding: '10px 12px', borderRadius: 6, border: `1px solid ${COLORS.border}`,
    fontSize: 15, width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = field
    ? { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: 'block', marginBottom: 5 }
    : { fontSize: 13, fontWeight: 600, color: COLORS.textDark, display: 'block', marginBottom: 4 };

  const inputEl = isLoaded && !KEY_MISSING ? (
    <Autocomplete
      onLoad={setAutocomplete}
      onPlaceChanged={onPlaceChanged}
      options={{ componentRestrictions: { country: 'no' }, fields: ['formatted_address', 'geometry', 'address_components'] }}
    >
      <input
        ref={inputRef}
        style={inputStyle}
        value={value.street}
        onFocus={() => setPanelOpen(true)}
        onBlur={closePanelOnBlur}
        onKeyDown={closePanelOnEscape}
        onChange={e => { setPanelOpen(false); onChange({ ...valueWithoutPin, street: e.target.value, lat: null, lng: null }); }}
        placeholder="Søk etter adresse…"
        required={required}
      />
    </Autocomplete>
  ) : (
    <input
      ref={inputRef}
      style={inputStyle}
      value={value.street}
      onFocus={() => setPanelOpen(true)}
      onBlur={closePanelOnBlur}
      onKeyDown={closePanelOnEscape}
      onChange={e => { setPanelOpen(false); onChange({ ...valueWithoutPin, street: e.target.value }); }}
      placeholder="Skriv inn adresse"
      required={required}
    />
  );

  return (
    <div>
      {(label || !field) && <label style={labelStyle}>{label}</label>}

      <div style={{ position: 'relative' }}>
        {field && icon && (
          <span style={{ position: 'absolute', left: 14, top: 0, height: 52, color: COLORS.brandPanel, display: 'flex', alignItems: 'center', zIndex: 1, pointerEvents: 'none' }}>
            {icon}
          </span>
        )}
        {inputEl}
        {showPanel && (
          // onMouseDown hindrer at feltet mister fokus før klikket registreres
          <div
            ref={panelRef}
            role="group"
            aria-label="Forslag"
            style={panelStyles.panel}
            onMouseDown={e => e.preventDefault()}
            onBlur={closePanelOnBlur}
            onKeyDown={closePanelOnEscape}
          >
            {allowMyLocation && (
              <button type="button" onClick={fillFromMyLocation} disabled={locating} style={panelStyles.row}>
                <span style={panelStyles.name}>{locating ? 'Henter posisjon…' : '📍 Min posisjon'}</span>
              </button>
            )}
            {groups.map(g => (
              <div key={g.title}>
                <p style={panelStyles.title}>{g.title}</p>
                {g.chips.map(chip => (
                  <button key={chip.key} type="button" onClick={() => pickSuggestion(chip)} style={panelStyles.row}>
                    <span style={panelStyles.nameRow}>
                      {chip.logoUrl && <SupplierLogo logoUrl={chip.logoUrl} size={18} />}
                      <span style={panelStyles.name}>{chip.name}</span>
                    </span>
                    {chip.sub && <span style={panelStyles.sub}>{chip.sub}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {((allowMyLocation && !panelMode) || pinAvailable) && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {allowMyLocation && !panelMode && (
            <button
              type="button"
              onClick={fillFromMyLocation}
              disabled={locating}
              style={{ background: 'none', border: 'none', padding: '6px 0 0', color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: locating ? 'default' : 'pointer' }}
            >
              {locating ? 'Henter posisjon…' : '📍 Min posisjon'}
            </button>
          )}
          {pinAvailable && (
            <button
              type="button"
              onClick={() => setPinOpen(true)}
              style={{ background: 'none', border: 'none', padding: '6px 0 0', color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              📌 Sett pin på kart
            </button>
          )}
        </div>
      )}
      {locError && <span role="alert" style={{ fontSize: 12, color: COLORS.dangerText, display: 'block', marginTop: 2 }}>{locError}</span>}

      {value.lat && (
        <span style={{ fontSize: 11, color: COLORS.success, marginTop: 2, display: 'block' }}>
          {value.pinned ? '✓ Satt med pin på kart' : '✓ Koordinater funnet'}
        </span>
      )}

      {pinOpen && (
        <MapPinPicker
          initial={value.lat != null && value.lng != null ? { lat: value.lat, lng: value.lng } : null}
          onClose={() => setPinOpen(false)}
          onConfirm={addr => { onChange(addr); setPinOpen(false); }}
        />
      )}
    </div>
  );
}

const panelStyles = {
  panel: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 30, maxHeight: 280, overflowY: 'auto',
    background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    padding: '4px 0',
  },
  title: { fontSize: 11, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.8, margin: '8px 14px 2px' },
  row: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%', padding: '10px 14px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: COLORS.textDark },
  nameRow: { display: 'flex', alignItems: 'center', gap: 6 },
  name: { fontSize: 14, fontWeight: 700 },
  sub: { fontSize: 12, color: COLORS.textMid },
};
